import React, { useEffect, useState, useCallback } from "react";
import httpRequest from "../../../../../ajax";
import ShareButton from "../ShareButton";

/**
 * LeaderboardWidget — persistent card at the top of the Workbrook tab.
 *
 * Per PRD §5.2:
 *   - Shows top 5 referrers with anonymized display usernames + tier badges
 *   - Monthly and all-time toggle
 *   - Pinned to the top of the feed (not part of the chronological stream)
 *
 * Per PRD §10 empty states:
 *   - No placements: 5 blurred placeholder rows, "The league starts here..."
 *   - Fewer than 5: actual referrers + "Open spot - could be you"
 *   - User not ranked: "Not ranked yet. Make your first referral to join the league."
 *   - Zero placements: "Aspiring Referrer" text, no badge
 */
const LeaderboardWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("monthly");

  const fetchLeaderboard = useCallback(
    async (selectedPeriod) => {
      setLoading(true);
      try {
        const res = await httpRequest("get", `/leaderboard?period=${selectedPeriod}`);
        if (res?.data?.status) {
          setData(res.data.data);
        }
      } catch (err) {
        console.error("LeaderboardWidget: fetch error", err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchLeaderboard(period);
  }, [period, fetchLeaderboard]);

  const handlePeriodToggle = (newPeriod) => {
    if (newPeriod !== period) setPeriod(newPeriod);
  };

  const tierStyles = {
    bronze:   { bg: "#f5e6d3", color: "#8b4513", label: "BRONZE" },
    silver:   { bg: "#e8e8e8", color: "#555",    label: "SILVER" },
    gold:     { bg: "#fef3c7", color: "#a16207", label: "GOLD" },
    platinum: { bg: "#e0f2fe", color: "#0369a1", label: "PLATINUM" },
  };

  const topReferrers = data?.top_referrers || [];
  const totalReferrers = data?.total_referrers || 0;
  const userRank = data?.user_rank;
  const userPlacementCount = data?.user_placement_count || 0;
  const userTier = data?.user_tier;
  const userOptedIn = data?.user_opted_in || false;

  // Build display rows: actual referrers + "Open spot" fillers
  const displayRows = [];
  for (let i = 0; i < 5; i++) {
    if (topReferrers[i]) {
      displayRows.push({ type: "referrer", data: topReferrers[i] });
    } else {
      displayRows.push({ type: "open", rank: i + 1 });
    }
  }

  return (
    <div style={styles.container}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3a6ed4" strokeWidth="2">
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
            <path d="M4 22h16" />
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
          </svg>
          <span style={styles.title}>Referrer League</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* ── Period Toggle ── */}
          <div style={styles.toggle}>
            <button
              style={period === "monthly" ? styles.toggleBtnActive : styles.toggleBtn}
              onClick={() => handlePeriodToggle("monthly")}
            >
              Monthly
            </button>
            <button
              style={period === "alltime" ? styles.toggleBtnActive : styles.toggleBtn}
              onClick={() => handlePeriodToggle("alltime")}
            >
              All-time
            </button>
          </div>
          <ShareButton
            url={`${window.location.origin}/league`}
            title="Workbrook Referrer League"
            text="Check out the Workbrook Referrer League leaderboard!"
          />
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={styles.loading}>
          <div style={styles.loadingSpinner} />
        </div>
      )}

      {/* ── Body ── */}
      {!loading && (
        <>
          {/* Empty state: no placements at all */}
          {totalReferrers === 0 ? (
            <div style={styles.emptyState}>
              {displayRows.map((row, i) => (
                <div key={i} style={styles.blurredRow} />
              ))}
              <p style={styles.emptyText}>
                The league starts here. Refer your first Tier 3 candidate to claim a spot.
              </p>
            </div>
          ) : (
            /* Normal: show referrers + open spots */
            <div style={styles.list}>
              {displayRows.map((row, i) => {
                if (row.type === "open") {
                  return (
                    <div key={i} style={styles.openRow}>
                      <span style={styles.rank}>#{row.rank}</span>
                      <span style={styles.openText}>Open spot — could be you</span>
                    </div>
                  );
                }
                const ts = tierStyles[row.data.tier] || tierStyles.bronze;
                return (
                  <div key={i} style={styles.referrerRow}>
                    <span style={styles.rank}>#{row.data.rank}</span>
                    <span style={styles.name}>{row.data.display_name}</span>
                    <span style={{ ...styles.tierBadge, backgroundColor: ts.bg, color: ts.color }}>
                      {ts.label}
                    </span>
                    <span style={styles.count}>{row.data.placement_count} placements</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── User's own rank ── */}
          {!loading && (
            <div style={styles.userRankSection}>
              {userPlacementCount === 0 ? (
                <div style={styles.userNotRanked}>
                  <span style={styles.aspiringText}>Aspiring Referrer</span>
                  <a href="/home" style={styles.referCta}>Make your first referral →</a>
                </div>
              ) : userRank ? (
                <div style={styles.userRanked}>
                  <span style={styles.userRankLabel}>
                    You #{userRank.rank}
                  </span>
                  <span style={{ ...styles.tierBadge, ...(tierStyles[userTier] || tierStyles.bronze) }}>
                    {(tierStyles[userTier] || tierStyles.bronze).label}
                  </span>
                  <span style={styles.count}>{userPlacementCount} placements</span>
                </div>
              ) : (
                <div style={styles.userNotRanked}>
                  <span style={styles.aspiringText}>
                    Not ranked yet this {period === "monthly" ? "month" : "period"}.
                  </span>
                  <a href="/jobs" style={styles.referCta}>Refer now →</a>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    marginBottom: "16px",
    padding: "18px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "14px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
  },
  toggle: {
    display: "flex",
    gap: "4px",
    backgroundColor: "#f1f5f9",
    borderRadius: "8px",
    padding: "2px",
  },
  toggleBtn: {
    background: "none",
    border: "none",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#64748b",
    cursor: "pointer",
    borderRadius: "6px",
  },
  toggleBtnActive: {
    background: "#fff",
    border: "none",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#3a6ed4",
    cursor: "pointer",
    borderRadius: "6px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: "30px",
  },
  loadingSpinner: {
    width: "24px",
    height: "24px",
    border: "3px solid #e2e8f0",
    borderTopColor: "#3a6ed4",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  referrerRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 0",
    borderBottom: "1px solid #f1f5f9",
  },
  openRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 0",
    borderBottom: "1px solid #f1f5f9",
    opacity: 0.6,
  },
  rank: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#94a3b8",
    minWidth: "28px",
  },
  name: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
    flex: 1,
  },
  openText: {
    fontSize: "13px",
    color: "#94a3b8",
    fontStyle: "italic",
    flex: 1,
  },
  tierBadge: {
    fontSize: "10px",
    fontWeight: "700",
    padding: "2px 8px",
    borderRadius: "4px",
    letterSpacing: "0.3px",
  },
  count: {
    fontSize: "12px",
    color: "#64748b",
    minWidth: "90px",
    textAlign: "right",
  },
  emptyState: {
    padding: "10px 0",
  },
  blurredRow: {
    height: "36px",
    borderRadius: "6px",
    backgroundColor: "#f1f5f9",
    marginBottom: "8px",
    filter: "blur(2px)",
  },
  emptyText: {
    fontSize: "13px",
    color: "#94a3b8",
    textAlign: "center",
    marginTop: "12px",
    lineHeight: 1.5,
  },
  userRankSection: {
    marginTop: "14px",
    paddingTop: "14px",
    borderTop: "1px solid #f1f5f9",
  },
  userRanked: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  userRankLabel: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#3a6ed4",
  },
  userNotRanked: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  aspiringText: {
    fontSize: "13px",
    color: "#94a3b8",
  },
  referCta: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#3a6ed4",
    textDecoration: "none",
  },
};

export default LeaderboardWidget;
